<template>
  <div class="admin-solicitudes">
    <div class="d-flex justify-content-between align-items-center mb-2">
      <h4 class="mb-0">Solicitudes de Vacaciones</h4>
      <div class="d-flex">
        <b-form-select v-model="filtroEstado" :options="opcionesEstado" size="sm" class="mr-1" style="width: 140px" @change="cargarSolicitudes" />
        <b-button
          v-if="filtroEstado === 'pendiente' && solicitudesSeleccionadas.length > 0"
          variant="success"
          size="sm"
          @click="aprobarSeleccionadas"
        >
          <feather-icon icon="CheckIcon" class="mr-50" />
          Aprobar ({{ solicitudesSeleccionadas.length }})
        </b-button>
      </div>
    </div>

    <div>
        <b-table
          :items="solicitudesMostrar"
          :fields="campos"
          responsive
          show-empty
          empty-text="No hay solicitudes"
          selectable
          select-mode="multi"
          @row-selected="onRowSelected"
          :tbody-tr-class="rowClass"
        >
          <template #cell(selected)="{ rowSelected }">
            <template v-if="filtroEstado === 'pendiente'">
              <feather-icon v-if="rowSelected" icon="CheckSquareIcon" class="text-primary" />
              <feather-icon v-else icon="SquareIcon" class="text-muted" />
            </template>
          </template>

          <template #cell(usuario)="data">
            <div>
              <strong>{{ data.item.usuario_nombre }}</strong>
              <br>
              <small class="text-muted">{{ data.item.usuario_nombre_completo }}</small>
            </div>
          </template>

          <template #cell(fecha)="data">
            {{ formatearFecha(data.item.fecha) }}
          </template>

          <template #cell(tipo)="data">
            <span class="text-capitalize">{{ data.item.tipo }}</span>
          </template>

          <template #cell(estado)="data">
            <b-badge :variant="getEstadoVariant(data.item.estado)">
              {{ data.item.estado }}
            </b-badge>
          </template>

          <template #cell(acciones)="data">
            <div v-if="data.item.estado === 'pendiente'" class="d-flex">
              <b-button variant="flat-success" size="sm" class="mr-50" @click="aprobar(data.item.id)">
                <feather-icon icon="CheckIcon" />
              </b-button>
              <b-button variant="flat-danger" size="sm" @click="abrirModalRechazo(data.item)">
                <feather-icon icon="XIcon" />
              </b-button>
            </div>
            <div v-else>
              <small class="text-muted">{{ data.item.aprobador_nombre }}</small>
            </div>
          </template>
        </b-table>
    </div>

    <!-- Modal Rechazo -->
    <b-modal
      v-model="mostrarModalRechazo"
      title="Rechazar Solicitud"
      ok-title="Rechazar"
      ok-variant="danger"
      cancel-title="Cancelar"
      centered
      @ok="rechazar"
    >
      <p>
        <strong>Usuario:</strong> {{ solicitudRechazar ? solicitudRechazar.usuario_nombre_completo : '' }}<br>
        <strong>Fecha:</strong> {{ solicitudRechazar ? formatearFecha(solicitudRechazar.fecha) : '' }}
      </p>

      <b-form-group label="Motivo del rechazo (opcional)">
        <b-form-textarea v-model="motivoRechazo" rows="3" placeholder="Indica el motivo..." />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import {
  BTable, BBadge, BButton, BModal, BFormGroup, BFormTextarea, BFormSelect
} from 'bootstrap-vue'
import { mapState, mapActions } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BTable,
    BBadge,
    BButton,
    BModal,
    BFormGroup,
    BFormTextarea,
    BFormSelect,
  },

  data() {
    return {
      filtroEstado: 'pendiente',
      solicitudesSeleccionadas: [],
      mostrarModalRechazo: false,
      solicitudRechazar: null,
      motivoRechazo: '',
      opcionesEstado: [
        { value: 'pendiente', text: 'Pendientes' },
        { value: 'aprobada', text: 'Aprobadas' },
        { value: 'rechazada', text: 'Rechazadas' },
        { value: '', text: 'Todas' },
      ],
      campos: [
        { key: 'selected', label: '' },
        { key: 'usuario', label: 'Usuario' },
        { key: 'fecha', label: 'Fecha', sortable: true },
        { key: 'tipo', label: 'Tipo' },
        { key: 'estado', label: 'Estado' },
        { key: 'acciones', label: '' },
      ],
    }
  },

  computed: {
    ...mapState('vacaciones', ['solicitudesPendientes', 'solicitudesTodas']),

    solicitudesMostrar() {
      return this.filtroEstado === 'pendiente' ? this.solicitudesPendientes : this.solicitudesTodas
    },
  },

  mounted() {
    this.cargarSolicitudes()
  },

  methods: {
    ...mapActions('vacaciones', [
      'fetchSolicitudesPendientes', 'fetchSolicitudesTodas',
      'aprobarSolicitud', 'aprobarSolicitudesBulk', 'rechazarSolicitud'
    ]),

    async cargarSolicitudes() {
      if (this.filtroEstado === 'pendiente') {
        await this.fetchSolicitudesPendientes()
      } else {
        await this.fetchSolicitudesTodas({
          estado: this.filtroEstado || undefined,
        })
      }
    },

    onRowSelected(items) {
      this.solicitudesSeleccionadas = items.map(i => i.id)
    },

    rowClass(item, type) {
      if (!item || type !== 'row') return
      if (this.solicitudesSeleccionadas.includes(item.id)) return 'table-primary'
    },

    async aprobar(id) {
      try {
        await this.aprobarSolicitud(id)
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Solicitud aprobada', icon: 'CheckIcon', variant: 'success' },
        })
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: { title: error.error || 'Error', icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      }
    },

    async aprobarSeleccionadas() {
      try {
        await this.aprobarSolicitudesBulk(this.solicitudesSeleccionadas)
        this.$toast({
          component: ToastificationContent,
          props: { title: `${this.solicitudesSeleccionadas.length} solicitudes aprobadas`, icon: 'CheckIcon', variant: 'success' },
        })
        this.solicitudesSeleccionadas = []
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: { title: error.error || 'Error', icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      }
    },

    abrirModalRechazo(solicitud) {
      this.solicitudRechazar = solicitud
      this.motivoRechazo = ''
      this.mostrarModalRechazo = true
    },

    async rechazar(bvModalEvt) {
      bvModalEvt.preventDefault()

      try {
        await this.rechazarSolicitud({
          id: this.solicitudRechazar.id,
          motivo: this.motivoRechazo,
        })
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Solicitud rechazada', icon: 'CheckIcon', variant: 'success' },
        })
        this.mostrarModalRechazo = false
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: { title: error.error || 'Error', icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      }
    },

    formatearFecha(fecha) {
      return new Date(fecha).toLocaleDateString('es-ES', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    },

    getEstadoVariant(estado) {
      const variantes = {
        pendiente: 'warning',
        aprobada: 'success',
        rechazada: 'secondary',
      }
      return variantes[estado] || 'secondary'
    },
  },
}
</script>
